import {
  harvestClarityValue,
  harvestGenerateValue,
  harvestNurtureValue,
} from 'constants/applications/harvest.constants'
import { SalesforceCloud } from 'constants/salesforce.constants'
import { DeepReadonly } from 'helpers/general-helpers'
import { SinglePartnerSupportProvider } from 'interfaces/api/portal/company-api'
import { RoleOption } from 'modules/dashboard/user/user-store'

export const PortalOptionsAdvanced = [
  {
    value: 'userManagement',
    text: 'applications.portalUserManagement',
  },
  {
    value: 'applicationManagement',
    text: 'applications.portalApplicationManagement',
  },
  {
    value: 'portalManagement',
    text: 'applications.portalPortalManagement',
  },
  {
    value: 'plumeCloudManagement',
    text: 'applications.portalPlumeCloudManagement',
  },
  {
    value: 'apiManagement',
    text: 'applications.portalApiManagement',
  },
  {
    value: 'networkManagement',
    text: 'applications.portalNetworkManagement',
  },
  {
    value: 'basicAccess',
    text: 'applications.portalBasicAccess',
  },
] as const

export const PortalAdvancedOptionsTranslation = [
  {
    value: 'standard',
    text: 'users.portalRoleSuperStandard',
    requiredOptions: ['basicAccess'],
  },
  {
    value: 'admin',
    text: 'users.portalRoleAdmin',
    requiredOptions: [
      'basicAccess',
      'userManagement',
      'applicationManagement',
      'portalManagement',
      'plumeCloudManagement',
    ],
  },
  {
    value: 'superAdmin',
    text: 'users.portalRoleSuperadmin',
    requiredOptions: [
      'basicAccess',
      'userManagement',
      'applicationManagement',
      'portalManagement',
      'plumeCloudManagement',
      'apiManagement',
      'networkManagement',
    ],
  },
] as const

const NocOptions = [
  {
    value: 'groupAdmin',
    text: 'users.appRoleAdmin',
  },
  {
    value: 'groupSupport',
    text: 'users.appRoleSupport',
  },
] as const

const CentralOptions = [
  {
    value: 'groupAdmin',
    text: 'users.appRoleAdmin',
  },
  {
    value: 'groupSupport',
    text: 'users.appRoleSupport',
  },
  {
    value: 'groupSupportTechnician',
    text: 'users.appRoleTechnician',
  },
] as const

const SignalOptions = [
  {
    value: 'partnerAdmin',
    text: 'applications.signalRolePartnerAdmin',
  },
  {
    value: 'partnerSupportManagerExternal',
    text: 'applications.signalRolePartnerSupportManagerExternal',
  },
  {
    value: 'partnerSupportManagerInternal',
    text: 'applications.signalRolePartnerSupportManagerInternal',
  },
  {
    value: 'partnerSupportEngineer',
    text: 'applications.signalRolePartnerSupportEngineer',
  },
]

export const HarvestOptions = [
  {
    value: harvestGenerateValue,
    text: 'applications.harvestGenerateRole',
  },
  {
    value: harvestNurtureValue,
    text: 'applications.harvestNurtureRole',
  },
  {
    value: harvestClarityValue,
    text: 'applications.harvestClarityRole',
  },
] as const

export const UpriseOptions = [
  {
    value: 'partnerAdmin',
    text: 'applications.uprisePartnerAdmin',
  },
  {
    value: 'partnerTechnician',
    text: 'applications.uprisePartnerTechnician',
  },
  {
    value: 'partnerSupport',
    text: 'applications.uprisePartnerSupport',
  },
] as const

export const PanoramaOptions = [
  {
    value: 'partnerAdmin',
    text: 'applications.panoramaPartnerAdmin',
  },
  {
    value: 'partnerSupport',
    text: 'applications.panoramaPartnerSupport',
  },
  {
    value: 'partnerSales',
    text: 'applications.panoramaPartnerSales',
  },
] as const

export const PrismOptions = [
  {
    value: 'partnerAdmin',
    text: 'applications.prismPartnerAdmin',
  },
  {
    value: 'partnerStandard',
    text: 'applications.prismPartnerStandard',
  },
] as const

type ApplicationConformation = {
  id: string
  name: string
  description: string
  url: string
  icon: string
  sfProductName?: string
  sfToolName?: string
  environment: string[]
  banner?: string
  extra?: string
  cloud?: SalesforceCloud
  roleOptions?: RoleOption[]
  defaultRole?: string
  featuresOptions?: RoleOption[]
  advancedOptionsTranslation?: {
    value: string
    text: string
    requiredOptions: string[]
  }[]
  advancedInfo?: string
  partnerSupportProvider?: SinglePartnerSupportProvider
}

const applicationsConstType = [
  {
    id: process.env.OKTA_CLIENT_ID,
    name: 'Portal access',
    description: 'applications.portalDesc',
    url: undefined as string,
    icon: 'public-resources.svg',
    sfProductName: '',
    sfToolName: '',
    environment: ['production', 'staging', 'development'] as string[],
    featuresOptions: PortalOptionsAdvanced as readonly RoleOption[],
    advancedOptionsTranslation: PortalAdvancedOptionsTranslation,
    advancedInfo: `${process.env.PORTAL_API_BASE_URL}/misc/salesforce-digital-experience-sso/app-roles`,
  },
  {
    id: '0oa2p55zr10DJR2Sa0h8',
    name: 'Plume Console (CI cloud)',
    description: 'applications.prismDescription',
    url: 'https://ci.platform-dev.plume.com',
    icon: 'frontline-express.svg',
    sfProductName: 'Operator Suite',
    sfToolName: 'Operator Suite',
    environment: ['development'] as string[],
    cloud: 'CI',
    roleOptions: PrismOptions as readonly RoleOption[],
  },
  {
    id: '0oa1wttz35zExbSPF358',
    name: 'Plume Console (Gamma cloud)',
    description: 'applications.prismDescription',
    url: 'https://gamma.platform.plume.com',
    icon: 'frontline-express.svg',
    sfProductName: 'Operator Suite',
    sfToolName: 'Operator Suite',
    environment: ['production'] as string[],
    cloud: 'Gamma',
    roleOptions: PrismOptions as readonly RoleOption[],
  },
  {
    id: '0oa1wttzfosve5tuh358',
    name: 'Plume Console (Kappa cloud)',
    description: 'applications.prismDescription',
    url: 'https://kappa.platform.plume.com',
    icon: 'frontline-express.svg',
    sfProductName: 'Operator Suite',
    sfToolName: 'Operator Suite',
    environment: ['production'] as string[],
    cloud: 'Kappa',
    roleOptions: PrismOptions as readonly RoleOption[],
  },
  {
    id: '0oa1wtttiqq48jh2e358',
    name: 'Plume Console (Beta cloud)',
    description: 'applications.prismDescription',
    url: 'https://beta.platform.plume.com',
    icon: 'frontline-express.svg',
    sfProductName: 'Operator Suite',
    sfToolName: 'Operator Suite',
    environment: ['production'] as string[],
    cloud: 'Beta',
    roleOptions: PrismOptions as readonly RoleOption[],
  },
  {
    id: '0oa1w9dp6egHVJIkC358',
    name: 'Plume Console (Dogfood cloud)',
    description: 'applications.prismDescription',
    url: 'https://dogfood.platform-stg.plume.com',
    icon: 'frontline-express.svg',
    sfProductName: 'Operator Suite',
    sfToolName: 'Operator Suite',
    environment: ['staging'] as string[],
    cloud: 'Dogfood',
    roleOptions: PrismOptions as readonly RoleOption[],
  },
  {
    id: '0oa1wtu3pb5SALPO8358',
    name: 'Plume Console (Phi cloud)',
    description: 'applications.prismDescription',
    url: 'https://phi.platform.plume.com',
    icon: 'frontline-express.svg',
    sfProductName: 'Operator Suite',
    sfToolName: 'Operator Suite',
    environment: ['production'] as string[],
    cloud: 'Phi',
    roleOptions: PrismOptions as readonly RoleOption[],
  },
  {
    id: '0oa2k9bcfdsfL6dm50h8',
    name: 'Frontline Lite (CI cloud)',
    description: 'applications.frontlineExpressDescription',
    url: 'https://ci.express-dev.plume.com/',
    icon: 'frontline-express.svg',
    sfProductName: 'Frontline Express',
    sfToolName: 'Frontline Express',
    environment: ['development'] as string[],
    cloud: 'CI',
    roleOptions: CentralOptions as readonly RoleOption[],
  },
  {
    id: '0oa1quuun35RrE21X358',
    name: 'Frontline Lite (Gamma cloud)',
    description: 'applications.frontlineExpressDescription',
    url: 'https://gamma.express.plume.com',
    icon: 'frontline-express.svg',
    sfProductName: 'Frontline Express',
    sfToolName: 'Frontline Express',
    environment: ['production'] as string[],
    cloud: 'Gamma',
    roleOptions: CentralOptions as readonly RoleOption[],
  },
  {
    id: '0oa1quuw7pa0l1AI5358',
    name: 'Frontline Lite (Kappa cloud)',
    description: 'applications.frontlineExpressDescription',
    url: 'https://kappa.express.plume.com',
    icon: 'frontline-express.svg',
    sfProductName: 'Frontline Express',
    sfToolName: 'Frontline Express',
    environment: ['production'] as string[],
    cloud: 'Kappa',
    roleOptions: CentralOptions as readonly RoleOption[],
  },
  {
    id: '0oa1quumffeeXqEeJ358',
    name: 'Frontline Lite (Beta Cloud)',
    description: 'applications.frontlineExpressDescription',
    url: 'https://beta.express.plume.com',
    icon: 'frontline-express.svg',
    environment: ['production', 'development'] as string[],
    sfProductName: 'Frontline Express',
    sfToolName: 'Frontline Express',
    cloud: 'Beta',
    roleOptions: CentralOptions as readonly RoleOption[],
  },
  {
    id: '0oa1quuvm96ueAgYp358',
    name: 'Frontline Lite (Iota cloud)',
    description: 'applications.frontlineExpressDescription',
    url: 'https://iota.express.plume.com',
    icon: 'frontline-express.svg',
    sfProductName: 'Frontline Express',
    sfToolName: 'Frontline Express',
    environment: ['production'] as string[],
    cloud: 'Iota',
    roleOptions: CentralOptions as readonly RoleOption[],
  },
  {
    id: '0oa1quurfalYUBhUV358',
    name: 'Frontline Lite (OpenSync Cloud)',
    description: 'applications.frontlineExpressDescription',
    url: 'https://opensync.express.plume.com',
    icon: 'frontline-express.svg',
    environment: ['production', 'staging', 'development'] as string[],
    sfProductName: 'Frontline Express',
    sfToolName: 'Frontline Express',
    cloud: 'OpenSync',
    roleOptions: CentralOptions as readonly RoleOption[],
  },
  {
    id: '0oa1quujtou2V4uRT358',
    name: 'Frontline Lite (Dogfood Cloud)',
    description: 'applications.frontlineExpressDescription',
    url: 'https://dogfood.express.plume.com',
    icon: 'frontline-express.svg',
    environment: ['production', 'staging', 'development'] as string[],
    sfProductName: 'Frontline Express',
    sfToolName: 'Frontline Express',
    cloud: 'Dogfood',
    roleOptions: CentralOptions as readonly RoleOption[],
  },
  {
    id: '0oa1quuy8rv36A2Ku358',
    name: 'Frontline Lite (Phi Cloud)',
    description: 'applications.frontlineExpressDescription',
    url: 'https://phi.express.plume.com',
    icon: 'frontline-express.svg',
    environment: ['production'] as string[],
    sfProductName: 'Frontline Express',
    sfToolName: 'Frontline Express',
    cloud: 'Phi',
    roleOptions: CentralOptions as readonly RoleOption[],
  },
  {
    id: '0oaja8eoq2vFt5nA10h7',
    name: 'Frontline Tier 1 (CI cloud)',
    description: 'applications.centralDesc',
    url: 'https://ci.central-dev.plume.com/',
    icon: 'network-central.svg',
    sfProductName: 'Frontline',
    sfToolName: 'Plume Central',
    environment: ['development'] as string[],
    cloud: 'CI',
    roleOptions: CentralOptions as readonly RoleOption[],
  },
  {
    id: '0oavlcw1ujirVSm5K356',
    name: 'Frontline Tier 1 (Gamma cloud)',
    description: 'applications.centralDesc',
    url: 'https://gamma.central.plume.com',
    icon: 'network-central.svg',
    sfProductName: 'Frontline',
    sfToolName: 'Plume Central',
    environment: ['production'] as string[],
    cloud: 'Gamma',
    roleOptions: CentralOptions as readonly RoleOption[],
  },
  {
    id: '0oaok2ovllN6YTDDa356',
    name: 'Frontline Tier 1 (Kappa cloud)',
    description: 'applications.centralDesc',
    url: 'https://kappa.central.plume.com',
    icon: 'network-central.svg',
    sfProductName: 'Frontline',
    sfToolName: 'Plume Central',
    environment: ['production'] as string[],
    cloud: 'Kappa',
    roleOptions: CentralOptions as readonly RoleOption[],
  },
  {
    id: '0oavliiygEcuep6ZE356',
    name: 'Frontline Tier 1 (Beta Cloud)',
    description: 'applications.centralDesc',
    url: 'https://beta.central.plume.com',
    icon: 'network-central.svg',
    environment: ['production', 'development'] as string[],
    sfProductName: 'Frontline',
    sfToolName: 'Plume Central Beta',
    cloud: 'Beta',
    roleOptions: CentralOptions as readonly RoleOption[],
  },
  {
    id: '0oa7xgnbx5tvV0i3L357',
    name: 'Frontline Tier 1 (Iota cloud)',
    description: 'applications.centralDesc',
    url: 'https://iota.central.plume.com',
    icon: 'network-central.svg',
    sfProductName: 'Frontline',
    sfToolName: 'Plume Central',
    environment: ['production'] as string[],
    cloud: 'Iota',
    roleOptions: CentralOptions as readonly RoleOption[],
  },
  {
    id: '0oavs57h7H6o8rLIs356',
    name: 'Frontline Tier 1 (OpenSync Cloud)',
    description: 'applications.centralDesc',
    url: 'https://opensync.central.plume.com',
    icon: 'network-central.svg',
    environment: ['production', 'staging', 'development'] as string[],
    sfProductName: 'Frontline',
    sfToolName: 'Plume Central Opensync',
    cloud: 'OpenSync',
    roleOptions: CentralOptions as readonly RoleOption[],
  },
  {
    id: '0oavlr5jgo4343Eym356',
    name: 'Frontline Tier 1 (Dogfood Cloud)',
    description: 'applications.centralDesc',
    url: 'https://dogfood.central.plume.com',
    icon: 'network-central.svg',
    environment: ['production', 'staging', 'development'] as string[],
    sfProductName: 'Frontline',
    sfToolName: 'Plume Central Dogfood',
    cloud: 'Dogfood',
    roleOptions: CentralOptions as readonly RoleOption[],
  },
  {
    id: '0oavlvkn1FNEZhF3g356',
    name: 'Frontline Tier 1 (Etna Cloud)',
    description: 'applications.centralDesc',
    url: 'https://etna.central.plume.com',
    icon: 'network-central.svg',
    environment: ['staging'] as string[],
    sfProductName: 'Frontline',
    sfToolName: 'Plume Central',
    cloud: 'dev-4',
    roleOptions: CentralOptions as readonly RoleOption[],
  },
  {
    id: '0oavsak8ajSCKvQNE356',
    name: 'Frontline Tier 1 (Thetadev Cloud)',
    description: 'applications.centralDesc',
    url: 'https://thetadev.central.plume.com',
    icon: 'network-central.svg',
    environment: ['staging'] as string[],
    sfProductName: 'Frontline',
    sfToolName: 'Plume Central',
    cloud: 'dev-8',
    roleOptions: CentralOptions as readonly RoleOption[],
  },
  {
    id: '0oa137qxqosavarP1358',
    name: 'Frontline Tier 1 (Phi Cloud)',
    description: 'applications.centralDesc',
    url: 'https://phi.central.plume.com',
    icon: 'network-central.svg',
    environment: ['production'] as string[],
    sfProductName: 'Frontline',
    sfToolName: 'Plume Central',
    cloud: 'Phi',
    roleOptions: CentralOptions as readonly RoleOption[],
  },
  {
    id: '0oajlnu7zq0fjP0i00h7',
    name: 'Frontline Tier 2 & 3 (CI cloud)',
    description: 'applications.nocDesc',
    url: 'https://ci.noc-dev.plume.com/',
    icon: 'network-noc.svg',
    sfProductName: 'Frontline',
    sfToolName: 'Plume NOC',
    environment: ['development'] as string[],
    cloud: 'CI',
    roleOptions: NocOptions as readonly RoleOption[],
  },
  {
    id: '0oa16a2cw1IIfsm7N357',
    name: 'Frontline Tier 2 & 3 (Gamma cloud)',
    description: 'applications.nocDesc',
    url: 'https://gamma.noc.plume.com/',
    icon: 'network-noc.svg',
    sfProductName: 'Frontline',
    sfToolName: 'Plume NOC',
    environment: ['production'] as string[],
    cloud: 'Gamma',
    roleOptions: NocOptions as readonly RoleOption[],
  },
  {
    id: '0oa16a23wujewFtSQ357',
    name: 'Frontline Tier 2 & 3 (Kappa cloud)',
    description: 'applications.nocDesc',
    url: 'https://kappa.noc.plume.com/',
    icon: 'network-noc.svg',
    sfProductName: 'Frontline',
    sfToolName: 'Plume NOC',
    environment: ['production'] as string[],
    cloud: 'Kappa',
    roleOptions: NocOptions as readonly RoleOption[],
  },
  {
    id: '0oa169rzos6ojMkSk357',
    name: 'Frontline Tier 2 & 3 (Beta Cloud)',
    description: 'applications.nocDesc',
    url: 'https://beta.noc.plume.com/',
    icon: 'network-noc.svg',
    environment: ['production', 'development'] as string[],
    sfProductName: 'Frontline',
    sfToolName: 'Plume NOC Beta',
    cloud: 'Beta',
    roleOptions: NocOptions as readonly RoleOption[],
  },
  {
    id: '0oa7xgko96Y6ZyX34357',
    name: 'Frontline Tier 2 & 3 (Iota cloud)',
    description: 'applications.nocDesc',
    url: 'https://iota.noc.plume.com/',
    icon: 'network-noc.svg',
    sfProductName: 'Frontline',
    sfToolName: 'Plume NOC',
    environment: ['production'] as string[],
    cloud: 'Iota',
    roleOptions: NocOptions as readonly RoleOption[],
  },
  {
    id: '0oa1b6l3tkwVQ1fye357',
    name: 'Frontline Tier 2 & 3 (OpenSync Cloud)',
    description: 'applications.nocDesc',
    url: 'https://opensync.noc.plume.com/',
    icon: 'network-noc.svg',
    environment: ['production', 'staging', 'development'] as string[],
    sfProductName: 'Frontline',
    sfToolName: 'Plume NOC Opensync',
    cloud: 'OpenSync',
    roleOptions: NocOptions as readonly RoleOption[],
  },
  {
    id: '0oacjghkdGWW0iZMs356',
    name: 'Frontline Tier 2 & 3 (Dogfood Cloud)',
    description: 'applications.nocDesc',
    url: 'https://dogfood.noc.plume.com',
    icon: 'network-noc.svg',
    environment: ['production', 'staging', 'development'] as string[],
    sfProductName: 'Frontline',
    sfToolName: 'Plume NOC Dogfood',
    cloud: 'Dogfood',
    roleOptions: NocOptions as readonly RoleOption[],
  },
  {
    id: '0oa137r6c8rJI6hUj358',
    name: 'Frontline Tier 2 & 3 (Phi Cloud)',
    description: 'applications.nocDesc',
    url: 'https://phi.noc.plume.com',
    icon: 'network-noc.svg',
    environment: ['production'] as string[],
    sfProductName: 'Frontline',
    sfToolName: 'Plume NOC',
    cloud: 'Phi',
    roleOptions: NocOptions as readonly RoleOption[],
  },
  {
    id: '0oa1d204zmgIMGTgh357',
    name: 'Frontline Tier 2 & 3 (Etna Cloud)',
    description: 'applications.nocDesc',
    url: 'https://etna.noc.plume.com',
    icon: 'network-noc.svg',
    environment: ['staging'] as string[],
    sfProductName: 'Frontline',
    sfToolName: 'Plume NOC',
    cloud: 'dev-4',
    roleOptions: NocOptions as readonly RoleOption[],
  },
  {
    id: '0oa19vn8zuKIIuex4357',
    name: 'Frontline Tier 2 & 3 (Thetadev Cloud)',
    description: 'applications.nocDesc',
    url: 'https://thetadev.noc.plume.com',
    icon: 'network-noc.svg',
    environment: ['staging'] as string[],
    sfProductName: 'Frontline',
    sfToolName: 'Plume NOC',
    cloud: 'dev-8',
    roleOptions: NocOptions as readonly RoleOption[],
  },
  {
    id: '0oa13azppxNN3mBYB357',
    name: 'Frontline Tier 2 & 3 (func4 Cloud)',
    description: 'applications.nocDesc',
    url: 'https://func4.noc-dev.plume.com',
    icon: 'network-noc.svg',
    environment: ['staging'] as string[],
    sfProductName: 'Frontline',
    sfToolName: 'Plume NOC',
    cloud: 'func4',
    roleOptions: NocOptions as readonly RoleOption[],
  },
  {
    id: '0oa12oet8abmbyIde0h8',
    name: 'Signal (CI cloud)',
    description: 'applications.signalDesc',
    url: 'https://ci.signal-stg.plume.com/',
    icon: 'signal.svg',
    sfProductName: 'Signal',
    sfToolName: 'Plume Signal',
    environment: ['development'] as string[],
    cloud: 'CI',
    roleOptions: SignalOptions as readonly RoleOption[],
    defaultRole: 'partnerSupportEngineer',
  },
  {
    id: '0oadbiswq5ZGHwdRI357',
    name: 'Signal (Kappa cloud)',
    description: 'applications.signalDesc',
    url: 'https://kappa.signal.plume.com/',
    icon: 'signal.svg',
    sfProductName: 'Signal',
    sfToolName: 'Plume Signal',
    environment: ['production'] as string[],
    cloud: 'Kappa',
    roleOptions: SignalOptions as readonly RoleOption[],
    defaultRole: 'partnerSupportEngineer',
  },
  {
    id: '0oadbiqmaw4nafGoX357',
    name: 'Signal (Gamma cloud)',
    description: 'applications.signalDesc',
    url: 'https://gamma.signal.plume.com',
    icon: 'signal.svg',
    sfProductName: 'Signal',
    sfToolName: 'Plume Signal',
    environment: ['production'] as string[],
    cloud: 'Gamma',
    roleOptions: SignalOptions as readonly RoleOption[],
    defaultRole: 'partnerSupportEngineer',
  },
  {
    id: '0oa17ggahjtsDDCNw358',
    name: 'Signal (Iota Cloud)',
    description: 'applications.signalDesc',
    url: 'https://iota.signal.plume.com',
    icon: 'signal.svg',
    sfProductName: 'Signal',
    sfToolName: 'Plume Signal',
    environment: ['production'] as string[],
    cloud: 'Iota',
    roleOptions: SignalOptions as readonly RoleOption[],
    defaultRole: 'partnerSupportEngineer',
  },
  {
    id: '0oa1koecepgtBHBcl358',
    name: 'Signal (Phi Cloud)',
    description: 'applications.signalDesc',
    url: 'https://phi.signal.plume.com',
    icon: 'signal.svg',
    sfProductName: 'Signal',
    sfToolName: 'Plume Signal',
    environment: ['production'] as string[],
    cloud: 'Phi',
    roleOptions: SignalOptions as readonly RoleOption[],
    defaultRole: 'partnerSupportEngineer',
  },
  {
    id: '0oadbini70Iov4IsU357',
    name: 'Signal (Beta Cloud)',
    description: 'applications.signalDesc',
    url: 'https://beta.signal.plume.com',
    icon: 'signal.svg',
    sfProductName: 'Signal',
    sfToolName: 'Plume Signal',
    environment: ['production'] as string[],
    cloud: 'Beta',
    roleOptions: SignalOptions as readonly RoleOption[],
    defaultRole: 'partnerSupportEngineer',
  },
  {
    id: '0oadnilc7yYAy4pya357',
    name: 'Signal Demo',
    description: 'applications.signalDesc',
    url: 'https://demo.signal.plume.com',
    icon: 'signal.svg',
    sfProductName: 'Signal Demo',
    sfToolName: 'Signal Demo',
    environment: ['production', 'staging'] as string[],
    banner: 'common.demo',
  },
  {
    id: '0oa21ivoo3ur88LCa0h8',
    name: 'Panorama',
    description: 'applications.tableauDesc',
    url: 'https://dev.panorama-dev.plume.com/',
    icon: 'network-haystack.svg',
    sfProductName: 'Panorama',
    sfToolName: 'Tableau',
    banner: 'common.new',
    environment: ['development', 'local'] as string[],
    roleOptions: PanoramaOptions as readonly RoleOption[],
  },
  {
    id: '0oa15gqd4rlAZMaFU358',
    name: 'Panorama',
    description: 'applications.tableauDesc',
    url: 'https://prod.panorama.plume.com',
    icon: 'network-haystack.svg',
    sfProductName: 'Panorama',
    sfToolName: 'Tableau',
    banner: 'common.new',
    environment: ['production', 'staging'] as string[],
    roleOptions: PanoramaOptions as readonly RoleOption[],
  },
  {
    id: '0oa15gowr3x9H7qHn0h8',
    name: 'Uprise (CI cloud)',
    description: 'applications.upriseDesc',
    url: 'https://external-dev.sso.plume.com/home/oidc_client/0oa15gowr3x9H7qHn0h8/aln5z7uhkbM6y7bMy0g7',
    icon: 'uprise.svg',
    sfProductName: 'Uprise',
    sfToolName: 'Uprise',
    environment: ['development'] as string[],
    cloud: 'CI',
    roleOptions: UpriseOptions as readonly RoleOption[],
  },
  {
    id: '0oagtwz2bbR6m3rLO357',
    name: 'Uprise (Gamma cloud)',
    description: 'applications.upriseDesc',
    url: 'https://external.sso.plume.com/home/oidc_client/0oagtwz2bbR6m3rLO357/aln177a159h7Zf52X0g8',
    icon: 'uprise.svg',
    sfProductName: 'Uprise',
    sfToolName: 'Uprise',
    environment: ['production'] as string[],
    cloud: 'Gamma',
    roleOptions: UpriseOptions as readonly RoleOption[],
  },
  {
    id: '0oagtx7w69VvLm3Ml357',
    name: 'Uprise (Kappa cloud)',
    description: 'applications.upriseDesc',
    url: 'https://external.sso.plume.com/home/oidc_client/0oagtx7w69VvLm3Ml357/aln177a159h7Zf52X0g8',
    icon: 'uprise.svg',
    sfProductName: 'Uprise',
    sfToolName: 'Uprise',
    environment: ['production'] as string[],
    cloud: 'Kappa',
    roleOptions: UpriseOptions as readonly RoleOption[],
  },
  {
    id: '0oagtwutdaC3GCiTI357',
    name: 'Uprise (Beta cloud)',
    description: 'applications.upriseDesc',
    url: 'https://beta.uprise.plume.com',
    icon: 'uprise.svg',
    sfProductName: 'Uprise',
    sfToolName: 'Uprise',
    environment: ['production'] as string[],
    cloud: 'Beta',
    roleOptions: UpriseOptions as readonly RoleOption[],
  },
  {
    id: '0oagtwnw7z7i84vvG357',
    name: 'Uprise (Dogfood cloud)',
    description: 'applications.upriseDesc',
    url: 'https://dogfood.uprise.plume.com',
    icon: 'uprise.svg',
    sfProductName: 'Uprise',
    sfToolName: 'Uprise',
    environment: ['production', 'staging'] as string[],
    cloud: 'Dogfood',
    roleOptions: UpriseOptions as readonly RoleOption[],
  },
  {
    id: '0oa15glqqc4MrpK8R0h8',
    name: 'Flex (CI cloud)',
    description: 'applications.flexDesc',
    url: 'https://ci.flex-dev.plume.com',
    icon: 'network-flex.svg',
    sfProductName: 'Flex',
    sfToolName: 'Flex',
    environment: ['development'] as string[],
    cloud: 'CI',
  },
  {
    id: '0oagvqwcvuXn9BOAo357',
    name: 'Flex (Kappa cloud)',
    description: 'applications.flexDesc',
    url: 'https://kappa.flex.plume.com',
    icon: 'network-flex.svg',
    sfProductName: 'Flex',
    sfToolName: 'Flex',
    environment: ['production'] as string[],
    cloud: 'Kappa',
  },
  {
    id: '0oagvqy1mbxOsPH6A357',
    name: 'Flex (Gamma cloud)',
    description: 'applications.flexDesc',
    url: 'https://gamma.flex.plume.com',
    icon: 'network-flex.svg',
    sfProductName: 'Flex',
    sfToolName: 'Flex',
    environment: ['production'] as string[],
    cloud: 'Gamma',
  },
  {
    id: '0oagvquy4fOybe5u9357',
    name: 'Flex (Beta Cloud)',
    description: 'applications.flexDesc',
    url: 'https://beta.flex.plume.com',
    icon: 'network-flex.svg',
    environment: ['production'] as string[],
    sfProductName: 'Flex',
    sfToolName: 'Flex Beta',
    cloud: 'Beta',
  },
  {
    id: '0oa15glvqezXd8EHR0h8',
    name: 'Engage (CI cloud)',
    description: 'applications.harvestDesc',
    url: 'https://external-dev.sso.plume.com/home/oidc_client/0oa15glvqezXd8EHR0h8/aln5z7uhkbM6y7bMy0g7',
    icon: 'harvest.svg',
    sfProductName: 'Harvest',
    sfToolName: 'Harvest',
    environment: ['development'] as string[],
    cloud: 'CI',
    featuresOptions: HarvestOptions as readonly RoleOption[],
  },
  {
    id: '0oagvr3jpqhV2GjKt357',
    name: 'Engage (Gamma cloud)',
    description: 'applications.harvestDesc',
    url: 'https://external.sso.plume.com/home/oidc_client/0oagvr3jpqhV2GjKt357/aln177a159h7Zf52X0g8',
    icon: 'harvest.svg',
    sfProductName: 'Harvest',
    sfToolName: 'Harvest',
    environment: ['production'] as string[],
    cloud: 'Gamma',
    featuresOptions: HarvestOptions as readonly RoleOption[],
  },
  {
    id: '0oagvr6khoU8ALxYZ357',
    name: 'Engage (Kappa cloud)',
    description: 'applications.harvestDesc',
    url: 'https://external.sso.plume.com/home/oidc_client/0oagvr6khoU8ALxYZ357/aln177a159h7Zf52X0g8',
    icon: 'harvest.svg',
    sfProductName: 'Harvest',
    sfToolName: 'Harvest',
    environment: ['production'] as string[],
    cloud: 'Kappa',
    featuresOptions: HarvestOptions as readonly RoleOption[],
  },
  {
    id: '0oa2lbynzo8K0MfUP0h8',
    name: 'Plume Studio',
    description: 'applications.plumeStudioDescription',
    url: 'https://studio-dev.plume.com',
    icon: 'plume-studio.svg',
    sfProductName: 'Plume Studio',
    sfToolName: 'Plume Studio',
    environment: ['development'] as string[],
  },
  {
    id: '0oa1srhyx7ccuuVaf358',
    name: 'Plume Studio',
    description: 'applications.plumeStudioDescription',
    url: 'https://studio.plume.com',
    icon: 'plume-studio.svg',
    sfProductName: 'Plume Studio',
    sfToolName: 'Plume Studio',
    environment: ['production', 'staging'] as string[],
  },
  {
    id: '0oa2mknsnq3ee7TSr0h8',
    name: 'Developer Portal',
    description: 'applications.developerPortalDescription',
    url: 'https://plume-web-developer-docusaurus-4caed5bddbad.herokuapp.com',
    icon: 'knowledge-base.svg',
    sfProductName: 'Developer Portal',
    sfToolName: 'Developer Portal',
    environment: ['development'] as string[],
  },
  {
    id: '0oa1td7z7vrTn7a4K358',
    name: 'Developer Portal',
    description: 'applications.developerPortalDescription',
    url: 'https://developer.plume.com',
    icon: 'knowledge-base.svg',
    sfProductName: 'Developer Portal',
    sfToolName: 'Developer Portal',
    environment: ['production', 'staging'] as string[],
  },
  {
    id: '0oa16fzds984VYAhk0h8',
    name: 'Deal Registration',
    description: 'applications.vartopiaDesc',
    url: 'https://external-dev.sso.plume.com/home/dev-741674_vartopia_1/0oa16fzds984VYAhk0h8/aln16fzk7i6pKMZu80h8/',
    icon: 'vartopia.svg',
    sfProductName: 'Deal Registration',
    sfToolName: 'Deal Registration',
    environment: ['development'] as string[],
  },
  {
    id: '0oahik202d6PCkkGl357',
    name: 'Deal Registration',
    description: 'applications.vartopiaDesc',
    url: 'https://external.sso.plume.com/home/plume_vartopia_1/0oahik202d6PCkkGl357/alnhikcuy6PfJq0cF357',
    icon: 'vartopia.svg',
    sfProductName: 'Deal Registration',
    sfToolName: 'Deal Registration',
    environment: ['production', 'staging'] as string[],
  },
  {
    id: '0oa1q1zpx7cWBzMdR0h8',
    name: 'Partner Support',
    description: 'applications.zendeskDesc',
    url: `https://external-dev.sso.plume.com/home/bookmark/0oa1q1zpx7cWBzMdR0h8/1280`,
    icon: 'network-zendesk.svg',
    sfProductName: 'Partner Support',
    sfToolName: 'Partner Support',
    partnerSupportProvider: 'Salesforce',
    environment: ['development'] as string[],
  },
  {
    id: '0oath06ukmQBJSH3t357',
    name: 'Partner Support',
    description: 'applications.zendeskDesc',
    url: `https://external.sso.plume.com/home/bookmark/0oath06ukmQBJSH3t357/2557`,
    icon: 'network-zendesk.svg',
    sfProductName: 'Partner Support',
    sfToolName: 'Partner Support',
    partnerSupportProvider: 'Salesforce',
    environment: ['staging'] as string[],
  },
  {
    id: '0oath02b5sOUI9O2H357',
    name: 'Partner Support',
    description: 'applications.zendeskDesc',
    url: `https://external.sso.plume.com/home/bookmark/0oath02b5sOUI9O2H357/2557`,
    icon: 'network-zendesk.svg',
    sfProductName: 'Partner Support',
    sfToolName: 'Partner Support',
    partnerSupportProvider: 'Salesforce',
    environment: ['production'] as string[],
  },
  {
    id: '0oa2sjn2r0bTsMn3H0h8',
    name: 'Partner Support (JSM)',
    description: 'applications.zendeskDesc',
    url: `https://partner-support-launcher.dev.plume.com`,
    icon: 'network-zendesk.svg',
    sfProductName: 'Partner Support',
    sfToolName: 'Partner Support',
    partnerSupportProvider: 'Atlassian',
    banner: 'common.new',
    environment: ['development'] as string[],
  },
  {
    id: '0oa1zc9bkd5C8DnLV358',
    name: 'Partner Support (JSM)',
    description: 'applications.zendeskDesc',
    url: `https://partner-support-launcher.stg.plume.com`,
    icon: 'network-zendesk.svg',
    sfProductName: 'Partner Support',
    sfToolName: 'Partner Support',
    partnerSupportProvider: 'Atlassian',
    banner: 'common.new',
    environment: ['staging'] as string[],
  },
  {
    id: '0oa1zc9f6kt27oEHP358',
    name: 'Partner Support (JSM)',
    description: 'applications.zendeskDesc',
    url: `https://partner-support-launcher.plume.com`,
    icon: 'network-zendesk.svg',
    sfProductName: 'Partner Support',
    sfToolName: 'Partner Support',
    partnerSupportProvider: 'Atlassian',
    banner: 'common.new',
    environment: ['production'] as string[],
  },
  {
    id: '0oa168jriu5MkfBny0h8',
    name: 'Marketing Center',
    description: 'applications.seismicDesc',
    url: 'https://external-dev.sso.plume.com/home/seismic/0oa168jriu5MkfBny0h8/aln1juoxrs0zyGcZr1d8/',
    icon: 'network-seismic.svg',
    sfProductName: 'Marketing Center',
    sfToolName: 'Marketing Center',
    environment: ['development'] as string[],
  },
  {
    id: '0oajmzg32dSM2lIeB357',
    name: 'Marketing Center',
    description: 'applications.seismicDesc',
    url: 'https://external.sso.plume.com/home/plume_seismictest_1/0oajmzg32dSM2lIeB357/alnjmzqu38YB2Emkz357',
    icon: 'network-seismic.svg',
    sfProductName: 'Marketing Center',
    sfToolName: 'Marketing Center',
    environment: ['production', 'staging'] as string[],
  },
  {
    id: '0oa1xihdt4swfK3hU0h8',
    name: 'Customer Acquisition Portal',
    description: 'applications.capDescription',
    url: 'https://cap.plume-develop.com/auth/sign-in',
    icon: 'network-seismic.svg',
    sfProductName: 'Customer Acquisition Portal',
    sfToolName: 'Customer Acquisition Portal',
    environment: [] as string[],
  },
  {
    id: '0oayucs4qpc085rPZ357',
    name: 'Customer Acquisition Portal',
    description: 'applications.capDescription',
    url: 'https://cap.plume-preprod.com',
    icon: 'network-seismic.svg',
    sfProductName: 'Customer Acquisition Portal',
    sfToolName: 'Customer Acquisition Portal',
    environment: [] as string[],
  },
  {
    id: '0oaz22t2btFLjgXyv357',
    name: 'Customer Acquisition Portal',
    description: 'applications.capDescription',
    url: 'https://cap.plume.com',
    icon: 'network-seismic.svg',
    sfProductName: 'Customer Acquisition Portal',
    sfToolName: 'Customer Acquisition Portal',
    environment: [] as string[],
  },
] as const satisfies DeepReadonly<ApplicationConformation[]>

type IApplicationRaw = (typeof applicationsConstType)[number]

type IApplicationArray = DeepReadonly<
  Array<
    ApplicationConformation & {
      name: IApplicationRaw['name']
      sfProductName?: IApplicationRaw['sfProductName']
      sfToolName?: IApplicationRaw['sfToolName']
    }
  >
>
export type IApplication = IApplicationArray[number]
export type ApplicationEnvironment = IApplication['environment']

export const applications: IApplicationArray = applicationsConstType

export function getApplicationById(appId: IApplication['id'] | (string & {})): IApplication {
  return applications.find(a => a.id === appId)
}

export const isCorrectAppEnvironment = (environment: ApplicationEnvironment) => {
  const portalEnv = process.env.PORTAL_ENV
  if (portalEnv === 'local' || portalEnv === 'test') {
    return environment.includes('development')
  } else {
    return environment.includes(portalEnv)
  }
}

export type AutoToggleAppsRecord = Record<
  string,
  {
    toggledSelection?: boolean
    toggledRole?: string
    targetDispatches: {
      appId: string
      selected?: boolean
      role?: string
    }[]
  }[]
>

export type DependentAppsRecord = Record<
  string,
  {
    dependentRole?: string
    dependencyAppId: string
    allowedRoles?: string[]
  }[]
>
